import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { MasterBlockId } from 'src/shared/types/base-entity-interface'
import { BorderStyleEnum } from 'common/types/styleTypes'
import {
  generateBaseEntity,
  generateHtmlAttrId,
  generateId,
} from 'src/shared/entities/utils/generate-base-entity'
import { ContactSlugEnum } from 'src/shared/enums/contact-fields-enum'
import {
  FormCheckboxInterface,
  FormSubentityTypeEnum,
  FormFieldInterface,
  FormInterface,
  FormLayoutEnum,
  FormSubmitButtonInterface,
} from 'src/shared/types/entities/form-interface'
import { RedirectionTypesEnum } from 'common/enums/ButtonActionEnum'
import { WebsiteSettingsTypographyTypeEnum } from 'src/shared/types/website-settings-types'
import i18n from 'client/i18n'
import { contactFieldLocoKeys } from '../../utils/get-contact-field-title'
import { TFunction } from 'i18next'
import { getEntityNameLocoKeyByEntityType } from '../../utils/get-entity-name-loco-key-by-entity-type'

export const createFormFieldSubentity = ({
  slug,
  t,
}: {
  slug: ContactSlugEnum
  t: TFunction
}): FormFieldInterface => ({
  id: generateId(),
  type: FormSubentityTypeEnum.Field,
  slug,
  htmlAttrId: generateHtmlAttrId(FormSubentityTypeEnum.Field),
  placeholder: t(contactFieldLocoKeys[slug]),
  optional: false,
})

export const createFormCheckboxSubentity = ({
  t,
}: {
  t: TFunction
}): FormCheckboxInterface => ({
  id: generateId(),
  type: FormSubentityTypeEnum.Checkbox,
  htmlAttrId: generateHtmlAttrId(FormSubentityTypeEnum.Checkbox),
  content: t('entities.explicit_consent.default_text'),
  padding: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 10,
    paddingRight: 0,
  },
  mobilePadding: {
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 8,
    paddingRight: 0,
  },
  errorText: t('settings_options.checkbox_message'),
  optional: false,
})

export const createFormSubmitButtonSubentity = ({
  t,
}: {
  t: TFunction
}): FormSubmitButtonInterface => ({
  id: generateId(),
  type: FormSubentityTypeEnum.SubmitButton,
  htmlAttrId: generateHtmlAttrId(FormSubentityTypeEnum.SubmitButton),
  text: t('entities.form.button.default_text'),
})

export const createForm = ({
  parentId,
  masterBlockId,
  locale,
}: {
  parentId: string
  masterBlockId?: MasterBlockId
  locale: string
}): FormInterface => {
  const t = i18n.getFixedT(locale)

  return {
    ...generateBaseEntity(EntityTypeEnum.Form, parentId, masterBlockId),
    customName: t(getEntityNameLocoKeyByEntityType(EntityTypeEnum.Form)),
    layout: FormLayoutEnum.OneColumn,
    rowGap: 12,
    mobileRowGap: 9,
    columnGap: 12,

    subEntities: [
      ...Object.values(ContactSlugEnum).map(slug =>
        createFormFieldSubentity({ slug, t }),
      ),
      createFormCheckboxSubentity({ t }),
      createFormSubmitButtonSubentity({ t }),
    ],
    fieldsCommonStyles: {},
    buttonCommonStyles: {
      buttonType: WebsiteSettingsTypographyTypeEnum.PrimaryButton,
      alignSelf: 'center',
    },
    checkboxesCommonStyles: {},
    action: {
      redirectionType: RedirectionTypesEnum.none,
      optInSuccessText: t('entities.explicit_consent.success_text'),
      doubleOptIn: false,
    },
    border: {
      width: 2,
      style: BorderStyleEnum.solid,
      color: 'rgb(226, 226, 227)',
      radius: 24,
    },
    padding: {
      paddingTop: 36,
      paddingBottom: 36,
      paddingLeft: 36,
      paddingRight: 36,
    },
    mobilePadding: {
      paddingTop: 10,
      paddingBottom: 10,
      paddingLeft: 10,
      paddingRight: 10,
    },
    margin: { marginTop: 0, marginBottom: 0, marginLeft: 0, marginRight: 0 },
    backgroundColor: 'rgb(255, 255, 255)',
  }
}
